import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo } from '../data/mockData';

const inquiryTypes = [
  'Media question',
  'Investor query',
  'Strategic partner discussion',
  'Pilot customer inquiry',
  'Licensing / IP commercialization',
  'Research / testbed / grant collaboration',
  'Other',
];

export default function MediaInvestorRelations() {
  const directEmail = companyInfo.emails.direct;
  const generalEmail = companyInfo.emails.general;

  const directMailto = useMemo(() => {
    const subject = 'Media / investor inquiry - SALAR Technologies LLC';
    const body = [
      'Hello SALAR Technologies LLC,',
      '',
      'I have a media, investor, partner, or pilot-customer inquiry.',
      '',
      'Name:',
      'Organization:',
      'Role / title:',
      'Inquiry type:',
      'Question:',
      '',
      'I understand this is a public-safe inquiry channel and will not include confidential or sensitive information.',
    ].join('\n');
    return `mailto:${directEmail}?cc=${generalEmail}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [directEmail, generalEmail]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = 'Media / investor inquiry - SALAR Technologies LLC';
    const body = [
      'Media / investor / partner inquiry for SALAR Technologies LLC',
      '',
      `Name: ${data.get('fullName') || ''}`,
      `Email: ${data.get('email') || ''}`,
      `Organization: ${data.get('organization') || ''}`,
      `Role / title: ${data.get('roleTitle') || ''}`,
      `Inquiry type: ${data.get('inquiryType') || ''}`,
      `Requested timing: ${data.get('requestedTiming') || ''}`,
      '',
      'Question / message:',
      data.get('message') || '',
      '',
      'Acknowledgement: I understand this is a public-safe inquiry channel.',
    ].join('\n');
    window.location.href = `mailto:${directEmail}?cc=${generalEmail}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <main className="min-h-screen bg-[#06101f] text-white" data-testid="media-investor-relations-page">
      <Helmet>
        <title>Media &amp; Investor Relations | SALAR Technologies LLC</title>
        <meta name="description" content="Public-safe media, investor, partner, and pilot-customer inquiry page for SALAR Technologies LLC." />
        <meta property="og:title" content="Media & Investor Relations | SALAR Technologies LLC" />
        <meta property="og:description" content="Request SALAR Technologies LLC media, investor, partner, licensing, or pilot-customer information. Public-safe inquiries route to vikrant@salartechnologies.com." />
        <meta property="og:url" content="https://www.salartechnologies.com/media-investor-relations" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/media-investor-relations" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="relative isolate overflow-hidden border-b border-cyan-300/10">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_22%,rgba(34,211,238,0.18),transparent_30%),radial-gradient(circle_at_82%_20%,rgba(245,158,11,0.10),transparent_28%),linear-gradient(120deg,#020617,#07162d_52%,#020617)]" />
        <div className="container mx-auto px-6 py-20 md:py-28">
          <div className="max-w-4xl">
            <Badge variant="outline" className="mb-5 border-cyan-300 text-cyan-200 tracking-widest">MEDIA / INVESTORS / PARTNERS</Badge>
            <h1 className="text-4xl font-black leading-tight tracking-tight md:text-6xl lg:text-7xl">Media &amp; Investor Relations</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-blue-100 md:text-xl">
              Public-safe inquiry path for media questions, qualified investor queries, strategic partner discussions, licensing inquiries, and pilot-customer requests related to SALAR Technologies LLC.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" className="bg-cyan-400 px-7 text-slate-950 hover:bg-cyan-300"><a href="#inquiry-form">Ask a Question <ArrowRight className="ml-2" size={18} /></a></Button>
              <Button asChild size="lg" variant="outline" className="border-cyan-300/50 bg-white/5 px-7 text-cyan-100 hover:bg-cyan-300/10"><a href={directMailto}>Email SALAR Directly <Mail className="ml-2" size={18} /></a></Button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-14">
        <div className="container mx-auto grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[
            ['Media Relations', 'Interview requests, article context, public-safe quotes, publication questions, and background on SALAR assurance intelligence.'],
            ['Investor Queries', 'Qualified relationship-building conversations around public-safe company context, market fit, and technology posture.'],
            ['Partners and Pilot Customers', 'Corporate partners, testbeds, primes, integrators, and pilot customers evaluating a focused 30-60 day assurance engagement.'],
            ['Public-Safe Only', 'Please keep the public form limited to non-sensitive questions and public-safe context.'],
          ].map(([title, body]) => (
            <Card key={title} className="border-cyan-300/20 bg-slate-950/60 text-white shadow-2xl shadow-blue-950/25">
              <CardContent className="p-6">
                <ShieldCheck className="mb-4 text-cyan-300" size={34} aria-hidden="true" />
                <h2 className="text-xl font-bold">{title}</h2>
                <p className="mt-3 text-sm leading-7 text-blue-100">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-y border-cyan-300/10 bg-slate-950/35 px-6 py-16">
        <div className="container mx-auto grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <aside className="rounded-3xl border border-cyan-300/20 bg-slate-950/70 p-6 shadow-2xl shadow-blue-950/30">
            <Sparkles className="mb-5 text-cyan-300" size={36} aria-hidden="true" />
            <h2 className="text-3xl font-bold">Direct contact path</h2>
            <p className="mt-4 leading-7 text-blue-100">This page composes an email to SALAR Technologies LLC and does not store inquiry data in the browser.</p>
            <div className="mt-6 space-y-3 rounded-2xl border border-cyan-300/15 bg-white/[0.04] p-5 text-sm leading-7 text-blue-100">
              <p><strong className="text-cyan-200">Primary email:</strong> {directEmail}</p>
              <p><strong className="text-cyan-200">General email:</strong> {generalEmail}</p>
              <p><strong className="text-cyan-200">Location:</strong> Wilsonville, Oregon</p>
              <p><strong className="text-cyan-200">Response target:</strong> within 12 hours when feasible.</p>
            </div>
            <a href={directMailto} className="mt-6 inline-flex items-center gap-2 rounded-xl border border-cyan-300/35 bg-cyan-300/10 px-5 py-3 font-semibold text-cyan-100 hover:bg-cyan-300/20">Draft email directly <Mail size={18} aria-hidden="true" /></a>
          </aside>

          <form id="inquiry-form" onSubmit={handleSubmit} className="rounded-3xl border border-cyan-300/20 bg-slate-950/70 p-6 shadow-2xl shadow-blue-950/30 md:p-8">
            <div className="mb-7">
              <h2 className="text-3xl font-bold">Ask SALAR a public-safe question</h2>
              <p className="mt-3 leading-7 text-blue-100">Submitting opens your email client with the completed question. Review the email before sending.</p>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <input name="fullName" required placeholder="Name" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white" />
              <input name="email" type="email" required placeholder="Email" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white" />
              <input name="organization" placeholder="Organization" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white" />
              <input name="roleTitle" placeholder="Role / title" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white" />
              <select name="inquiryType" required className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white">
                <option value="">Inquiry type</option>
                {inquiryTypes.map((item) => <option key={item}>{item}</option>)}
              </select>
              <select name="requestedTiming" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white">
                <option>Within 12 hours if feasible</option>
                <option>Within 1 business day</option>
                <option>This week</option>
                <option>No urgency</option>
              </select>
            </div>
            <textarea name="message" rows="7" required placeholder="Question / message" className="mt-6 w-full rounded-xl border border-cyan-300/20 bg-slate-950/80 px-4 py-3 text-white" />
            <label className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-4 text-sm leading-6 text-amber-100">
              <input type="checkbox" required className="mt-1 h-4 w-4 flex-shrink-0 accent-cyan-300" />
              <span>I will keep this inquiry public-safe and non-sensitive.</span>
            </label>
            <Button type="submit" size="lg" className="mt-7 bg-cyan-400 text-slate-950 hover:bg-cyan-300">Compose Inquiry Email <ArrowRight className="ml-2" size={18} /></Button>
          </form>
        </div>
      </section>
    </main>
  );
}
